import { useEffect, useState } from "react";

import { shuffle } from "lodash-es";

import { ChatLoadingSpinner } from "src/base-components/Chat/ChatLoadingSpinner";
import { ChatLoadingMode } from "src/base-components/Chat/types";
import { assertUnreachable } from "src/utils/typeUtils";

export const loadingTexts = [
  "Analyzing your request...",
  "Crafting a response...",
  "Thinking it through...",
  "Processing data...",
  "Generating insights...",
  "Preparing assistance...",
  "Computing solution...",
  "Decoding your query...",
  "Synthesizing answer...",
  "Formulating response...",
] as const;

export const retryingTexts = [
  "Retrying...",
  "Giving it another shot...",
  "Attempting again...",
  "Second try in progress...",
  "Reattempting solution...",
  "Taking another crack at it...",
  "Recalculating response...",
  "Round two, here we go...",
  "Regrouping and retrying...",
  "One more attempt...",
  "Refining and retrying...",
  "Another go at it...",
  "Reconfiguring and retrying...",
  "Back to the drawing board...",
  "Persistence mode: engaged...",
] as const;

const regeneratingText = "Regenerating response...";

/**
 * Kept in step with the agent execution log's thinking indicator, so both
 * surfaces advance their label at the same pace.
 */
const TEXT_ROTATION_INTERVAL_MS = 4500;

/** Counts elapsed rotation intervals since the indicator appeared. */
const useRotationStep = (): number => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(
      () => setStep((prev) => prev + 1),
      TEXT_ROTATION_INTERVAL_MS,
    );

    return () => clearInterval(interval);
  }, []);

  return step;
};

const getText = (
  mode: ChatLoadingMode,
  loadingOrder: string[],
  retryingOrder: string[],
  step: number,
): string => {
  switch (mode) {
    case "booting":
      return "Booting...";
    case "loading":
      return loadingOrder[step % loadingOrder.length];
    case "retrying":
      return retryingOrder[step % retryingOrder.length];
    case "regenerating":
      return regeneratingText;
    default:
      return assertUnreachable(mode);
  }
};

type ChatLoadingIndicatorProps = {
  mode: ChatLoadingMode;
  dataLoc?: string;
};

export const ChatLoadingIndicator: React.FC<ChatLoadingIndicatorProps> = ({
  mode,
  dataLoc,
}) => {
  // Shuffled once per mount: cycling a shuffled pool keeps the first label as
  // random as it always was, and shows every other one before repeating.
  const [loadingOrder] = useState(() => shuffle(loadingTexts));
  const [retryingOrder] = useState(() => shuffle(retryingTexts));
  const step = useRotationStep();
  const text = getText(mode, loadingOrder, retryingOrder, step);

  return (
    <li className="flex flex-col gap-y-2" data-loc={dataLoc}>
      <ChatLoadingSpinner text={text} />
    </li>
  );
};
